import { ResultResolve } from "@pdfking/errors"
import { BigintQ, NullQ, StringQ, BytesQ } from "@pdfking/structs"
import { JSONEncode, SafePromise } from "@pdfking/tfunc"

import { PACKAGES } from "./Packages"
import type { PackageSlug } from "./Packages"

// What a value prints as: strings quoted, a Map/Set/Uint8Array as its
// entries, a bigint with its `n`; the rest as JSON.
const Inspect = (value: unknown): string =>
  BigintQ(value)
    ? `${value}n`
    : value instanceof Map || value instanceof Set || BytesQ(value)
      ? `${value.constructor.name}(${Inspect([...value])})`
      : (JSONEncode(value, { bigint: true }) ?? String(value))

const AsyncFunction = (async () => {}).constructor as new (
  ...parameters: string[]
) => (...values: unknown[]) => Promise<void>

/**
 * Runs an example compiled by CompileExample, handing `print` each line as it
 * comes: its console output, each shown top-level value, and the error that
 * stopped it.
 */
export const RunExample = async (js: string, print: (line: string) => void): Promise<void> => {
  const log = (...values: unknown[]) =>
    print(values.map(value => (StringQ(value) ? value : Inspect(value))).join(" "))
  const run = new AsyncFunction("require", "console", "__show", js)
  const result = await SafePromise(
    run(
      // an example's `await require("@pdfking/tfunc")`: the package, loaded then
      (name: string) => PACKAGES[name.replace("@pdfking/", "") as PackageSlug](),
      { log, info: log, warn: log, error: log },
      (value: unknown) => NullQ(value) || print(Inspect(value))
    )
  )
  // a thrown Error comes back wrapped (INTERNAL_ERROR): print the Error
  ResultResolve(result, error => print(String(error.error ?? error.enum)))
}
